// Riga "Buono pasto auto" nelle viste di approvazione del titolare.
// Compare solo se il collaboratore ha soglia e importo configurati per la
// tipologia. Confronta le ore approvate al collaboratore (non quelle fatturate
// al cliente) con la soglia; il toggle permette di escludere il buono per il
// singolo report e viene inviato come `applicaBuonoPasto` nella revisione.
import React from 'react';
import { Utensils, ToggleLeft, ToggleRight } from 'lucide-react';

interface Props {
    /** Ore di lavoro correnti per il report. Per gli incarichi: ore approvate
     *  al collaboratore (oreApprovateTotali se valorizzato, altrimenti
     *  oreGps + oreExtraApprovate). Per i turni fiduciari: ore effettive del
     *  turno calcolate dai timestamp + override oreCollaboratore. */
    oreLavorate: number;
    /** Soglia ore configurata sul collaboratore per la tipologia. 0 / null → buono non configurato. */
    sogliaOre?: number | null;
    /** Importo singolo buono pasto configurato sul collaboratore. 0 / null → buono non configurato. */
    importo?: number | null;
    /** Toggle attuale del titolare per questo report. Default true. */
    applica: boolean;
    onChangeApplica: (v: boolean) => void;
}

export const BuonoPastoAutoCard: React.FC<Props> = ({ oreLavorate, sogliaOre, importo, applica, onChangeApplica }) => {
    const soglia = Number(sogliaOre) || 0;
    const imp = Number(importo) || 0;
    // Buono non configurato per questa tipologia: non si mostra nulla.
    if (soglia <= 0 || imp <= 0) return null;

    // Un buono ogni X ore (soglia 5h, 20h lavorate = 4 buoni); epsilon per i
    // multipli esatti. Deve combaciare con il calcolo del backend.
    const numBuoni = Math.floor(((Number(oreLavorate) || 0) / soglia) + 1e-9);
    const maturato = numBuoni >= 1;
    const totale = imp * numBuoni;
    const attivo = maturato && applica;

    return (
        <div className={`flex items-center gap-3 p-2.5 rounded-lg border ${attivo
            ? 'bg-amber-50 border-amber-200'
            : maturato
                ? 'bg-slate-50 border-slate-200'
                : 'bg-slate-50/50 border-slate-100'}`}>
            <div className={`p-1.5 rounded ${attivo ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500'}`}>
                <Utensils size={14}/>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-700">{numBuoni > 1 ? `Buono pasto ×${numBuoni}` : 'Buono pasto'}</p>
                <p className="text-[10px] text-slate-500">
                    {maturato
                        ? `Ore ${oreLavorate.toFixed(1)}h · 1 ogni ${soglia}h → ${numBuoni} × € ${imp.toFixed(2)} = € ${totale.toFixed(2)}`
                        : `Non maturato (ore ${oreLavorate.toFixed(1)}h / soglia ${soglia}h)`}
                </p>
            </div>
            {maturato && (
                <button
                    type="button"
                    onClick={() => onChangeApplica(!applica)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide border ${applica
                        ? 'bg-amber-100 border-amber-200 text-amber-800 hover:bg-amber-200'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                    title={applica ? 'Click per non applicare il buono in questo report' : 'Click per applicare il buono'}
                >
                    {applica ? <ToggleRight size={14}/> : <ToggleLeft size={14}/>}
                    {applica ? `+ € ${totale.toFixed(2)}` : 'Disattivato'}
                </button>
            )}
        </div>
    );
};

/** Ore effettive di un turno fiduciario per il check buono pasto: override
 *  collaboratore se valorizzato, altrimenti (effettivoFine - effettivoInizio)
 *  + oreExtraApprovate. Restituisce 0 se mancano i timestamp. */
export const oreEffettiveTurnoForBuonoPasto = (turno: any, oreExtraApprovate: number): number => {
    if (!turno) return 0;
    if (turno.oreCollaboratoreOverride != null) return Number(turno.oreCollaboratoreOverride);
    const ini = turno.effettivoInizio ? new Date(turno.effettivoInizio).getTime() : null;
    const fin = turno.effettivoFine ? new Date(turno.effettivoFine).getTime() : null;
    if (!ini || !fin || fin <= ini) return 0;
    const ore = Math.round(((fin - ini) / 3_600_000) * 10) / 10;
    return ore + (Number(oreExtraApprovate) || 0);
};
